"use client";

import React, { useEffect, useState } from "react";
import { Text } from "@radix-ui/themes";
import { format, isAfter, isBefore, startOfDay, endOfDay } from "date-fns";
import { CalendarIcon } from "@radix-ui/react-icons";
import { Cn } from "components/utils/Cn";
import { ShadButton } from "components/buttons/ShadButton";
import { Calendar } from "components/calendar/Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/popover/Popover";
import "../../tailwind.css";
import { ChevronDownIcon } from "@radix-ui/react-icons";

export function DatePickerWithRange({ className, value, onDateChange, 
  disableBeforeDate = new Date("2024-09-01"), numberOfMonths = 2, allowFutureDates = false, showEndDateOnOpen = false }) {
  const today = startOfDay(new Date());
  const before = startOfDay(disableBeforeDate);
  const [date, setDate] = useState(value || { from: null, to: null });
  const [month, setMonth] = useState(new Date());

  useEffect(() => { 
    if (value) { 
      setDate(value);
      if (showEndDateOnOpen && value.to) {
        // Show the end date month (adjusted for numberOfMonths to show end date on the right calendar)
        const endDate = new Date(value.to);
        endDate.setMonth(endDate.getMonth() - (numberOfMonths - 1));
        setMonth(endDate);
      } else if (value.from) {
        setMonth(value.from);
      }
    }
  }, [value, showEndDateOnOpen, numberOfMonths]);

  const handleDateChange = (newDate) => {
    if (newDate?.from) {
      if (!newDate.to || newDate.from.getTime() === newDate.to.getTime()) {
        newDate = {
          from: startOfDay(newDate.from),
          to: endOfDay(newDate.from),
        };
      } else {
        newDate = {
          from: startOfDay(newDate.from),
          to: endOfDay(newDate.to),
        };
      }

      if (!allowFutureDates && newDate.to.getTime() === endOfDay(today).getTime()) {
        newDate.to = new Date();
      }
    } else {
      // Date was cleared - update state and notify parent
      const emptyDate = { from: null, to: null };
      setDate(emptyDate);
      if (onDateChange) {
        onDateChange(emptyDate);
      }
      return;
    }

    setDate(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  return (
    <>
      <style>{`
        .date-picker-button {
          background-color: ${allowFutureDates ? 'none': 'var(--gray-a3)'};
          border: ${allowFutureDates ? '1px solid var(--gray-a7)': 'none'};
          transition: background-color 200ms;
          cursor: default;
        }
        .date-picker-button:hover {
          background-color: var(--gray-a4);
        }
      `}</style>
      <div className={Cn("grid gap-2", className)}>
        <Popover>
          <PopoverTrigger asChild>
            <ShadButton id="date" variant={"outline"}
              className={Cn("h-8 justify-between text-left date-picker-button gap-2", !date && "text-muted-foreground")} >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CalendarIcon width={16} height={16} color="var(--gray-11)" />
                <Text weight="medium" style={{ color: "var(--gray-11)" }}>
                  {date?.from ? (
                    date.to ? (
                      <React.Fragment>
                        {format(date.from, "dd LLL y")} - {format(date.to, "dd LLL y")}
                      </React.Fragment>
                    ) : (
                      format(date.from, "LLL dd, y")
                    )
                  ) : (
                    <span>Select a date range</span>
                  )}
                </Text>
              </div>
              <ChevronDownIcon style={{ color: "var(--gray-11)" }} />
            </ShadButton>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" size="2" 
            side={allowFutureDates ? "right": "bottom"}>
            <Calendar 
              initialFocus mode="range" 
              selected={date} 
              onSelect={handleDateChange} 
              numberOfMonths={numberOfMonths}
              month={month}
              onMonthChange={setMonth}
              disabled={(date) => {
                // Always disable dates before the specified date
                if (isBefore(date, before)) return true;
                
                // If allowFutureDates is false, disable dates after today
                if (!allowFutureDates && isAfter(date, today)) return true;
                
                return false;
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}
